<template>
  <div class="pallet-list" v-loading="loadingData">
    <div class="header-buttons" v-if="showHeaderOps">
      <el-button type="primary" plain size="mini" @click="refreshPage">
        Refresh
      </el-button>
    </div>
    <basic-table
      :maxHeight="maxHeight"
      :table-data="palletsPage"
      :table-headers="tableHeaders"
      :has-actions="showActions"
      :sortChange="sortChange"
      :paginationChange="paginationChange"
      :actionWidth="actionWidth"
      :showPagination="showPagination"
      :rowClassName="rowClassName"
      :stripe="!rowClassName"
      :summary="true"
      :hasRowExpand="hasRowExpand"
      :selectable="selectable"
      :handle-selection-change="handleSelectionChange"
    >
      <template v-slot:row-actions="props">
        <div class="actions f-r">
          <el-button
            v-if="showLabelAction"
            size="small"
            type="text"
            @click="() => generatePalletLabels(props.row)"
          >
            Label
          </el-button>
          <slot
            name="actions"
            v-bind:row="props.row"
            v-bind:index="props.index"
          />
        </div>
      </template>
      <template v-slot:row-expand="props">
        <slot
          name="row-expand"
          v-bind:row="props.row"
          v-bind:index="props.index"
        >
        </slot>
      </template>
      <template v-slot:footer-ops>
        <el-button
          type="success"
          plain
          size="mini"
          @click="releasePallets"
          :disabled="!selectedPallets.length"
          v-if="selectable && releasable"
        >
          Release
        </el-button>
        <el-button type="primary" plain size="mini" @click="refreshPage">
          Refresh
        </el-button>
      </template>
    </basic-table>
    <pallets-release ref="palletsRelease"></pallets-release>
  </div>
</template>
<script>
import debounce from 'lodash/debounce'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
// mixin
import { pageJumps } from '@/core/mixin/pages'
// models
import { NotIncludeDeleted, PalletTableHeaders } from '@/core/models/pallet'
// components
import BasicTable from '@/components/common/BasicTable.vue'
import PalletsRelease from '@/components/shipment/PalletsRelease.vue'
// util
import {
  Ascending,
  DefaultPageSize,
  Descending,
  getDefaultSort,
  PageCurrentKey,
  PageSizeKey
} from '@/core/configs/types'
import { viewPdf } from '@/core/utils'
// api
import { labelPallet, queryPallets } from '@/api/pallet'
import { queryAddress } from '@/api/address'
export default {
  name: 'PalletList',
  mixins: [pageJumps],
  props: {
    palletQuery: {
      type: Object,
      default: null
    },
    refresh: {
      type: Boolean,
      default: false
    },
    showActions: {
      type: Boolean,
      default: true
    },
    showGroupName: {
      type: Boolean,
      default: true
    },
    showPagination: {
      type: Boolean,
      default: true
    },
    showHeaderOps: {
      type: Boolean,
      default: true
    },
    showLabelAction: {
      type: Boolean,
      default: true
    },
    actionWidth: {
      type: Number,
      default: 80
    },
    maxHeight: {
      type: Number,
      default: -1
    },
    rowClassName: {
      type: Function,
      default: () => {}
    },
    excludeCols: {
      type: Array,
      default: () => []
    },
    selectable: {
      type: Boolean,
      default: false
    },
    releasable: {
      type: Boolean,
      default: false
    },
    hasRowExpand: {
      type: Boolean,
      default: false
    }
  },
  components: {
    BasicTable,
    PalletsRelease
  },
  data() {
    return {
      PalletTableHeaders,
      palletList: null,
      pagination: {
        [PageSizeKey]: this.showPagination ? DefaultPageSize : -1,
        [PageCurrentKey]: 1,
        total: 0,
        asc: '',
        desc: ''
      },
      loadingData: false,
      debouncedQuery: debounce(this.fetchPallets, 300),
      prevQuery: null,
      palletLoadedSlot: 'palletLoaded',
      selectedPallets: []
    }
  },
  computed: {
    palletsPage() {
      return {
        records: this.palletList,
        ...this.pagination
      }
    },
    tableHeaders() {
      const toExclude = [...this.excludeCols]
      if (!this.showGroupName) toExclude.push('groupName')
      return PalletTableHeaders.filter(one => !toExclude.includes(one.key))
    },
    fetchQuery() {
      return {
        ...this.palletQuery,
        ...this.pagination,
        statusList: NotIncludeDeleted
      }
    }
  },
  methods: {
    sortChange(sort) {
      if (sort.order === Ascending) {
        this.pagination.asc = sort.col
        this.pagination.desc = ''
      } else if (sort.order === Descending) {
        this.pagination.desc = sort.col
        this.pagination.asc = ''
      }
      this.debouncedQuery()
    },
    paginationChange(key, path, value) {
      this.pagination[key] = value
      this.debouncedQuery()
    },
    refreshPage() {
      this.debouncedQuery()
    },
    async fetchPallets() {
      this.loadingData = true
      getDefaultSort(PalletTableHeaders, this.pagination)
      let page
      if (!isEmpty(this.palletQuery)) {
        page = await queryPallets(this.fetchQuery)
      }
      const records = page?.records ?? []
      // search address
      const addressIds = records.map(one => one.addressId)
      if (addressIds.length > 0) {
        const addressPage = await queryAddress({ addressIds, size: -1 })
        const addressMap = Object.fromEntries(
          addressPage.records.map(one => [one.id, one])
        )
        records.forEach(one => (one.address = addressMap[one.addressId]))
      }
      this.$set(this, 'palletList', records)
      this.pagination.total = page?.total ?? 0
      this.loadingData = false
    },
    async generatePalletLabels(pallet) {
      const blob = await labelPallet(pallet.no)
      viewPdf(blob)
    },
    handleSelectionChange(selectedPallets) {
      this.$set(this, 'selectedPallets', selectedPallets)
    },
    releasePallets() {
      if (!this.releasable || this.selectedPallets.length === 0) return
      const addressCnt = (new Set(this.selectedPallets.map(p => p?.address?.id))).size
      if (addressCnt > 1) {
        this.$message.error('Cannot release pallets with different addresses!')
        this.$set(this, 'selectedPallets', [])
        return
      }
      this.$refs.palletsRelease.open(this.selectedPallets)
    }
  },
  watch: {
    refresh: {
      handler() {
        this.debouncedQuery()
      },
      immediate: true
    },
    palletQuery: {
      handler(one, two) {
        if (isEqual(one, two)) return
        this.debouncedQuery()
      },
      deep: true
    }
  }
}
</script>
<style lang="scss" scoped>
@import '@/assets/styles/common.scss';

.header-buttons {
  margin-bottom: 10px;
}
</style>
